import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export const AdminContact = () => {
  const [content, setContent] = useState({
    tagline: '',
    title: '',
    description: '',
    email: '',
    handwritten_message: '',
    signature: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/contact`);
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/contact`, { ...content, id: 'contact' }, getAuthHeaders());
      toast.success('Contact section saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="admin-contact-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif">Contact Section</h1>
          <p className="text-muted-foreground">Edit contact section content</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchContent} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-border p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Tagline</label>
            <input
              type="text"
              value={content.tagline}
              onChange={(e) => handleChange('tagline', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Email Address</label>
            <input
              type="email"
              value={content.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Section Title</label>
          <input
            type="text"
            value={content.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Description</label>
          <textarea
            value={content.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none resize-none"
          />
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="font-medium mb-4">Handwritten Sign-off</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Message</label>
              <input
                type="text"
                value={content.handwritten_message}
                onChange={(e) => handleChange('handwritten_message', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
                placeholder="e.g., I can't wait to hear you sing!"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Signature</label>
              <input
                type="text"
                value={content.signature}
                onChange={(e) => handleChange('signature', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
                placeholder="e.g., Kerry x"
              />
            </div>
          </div>
          
          {/* Preview */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-muted-foreground mb-1">Preview:</p>
            <p className="italic text-lg" style={{ fontFamily: "'Caveat', cursive" }}>{content.handwritten_message}</p>
            <p className="text-accent-gold text-xl" style={{ fontFamily: "'Caveat', cursive" }}>{content.signature}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContact;
